<template>
  <sp-text-field
    readonly
    :clearable="clearable"
    :persistent-clear="clearable"
    :disabled="disabled"
    class="activator"
    color="surface"
    :value.prop="value"
    :aria-label="label"
    :placeholder="placeholder"
    part="text-field"
    :input-test-id="inputTestId"
    propagate-append-click
    @click-clear="reportClear"
  >
    <sp-icon
      v-if="prependIcon"
      slot="prepend"
      class="default-filter__activator-icon"
      :name="prependIcon"
      fill-color="var(--sp-comp-filter-default-activator-icon-fill-color)"
    ></sp-icon>
    <sp-icon v-if="appendIcon" slot="append" class="default-filter__append-icon" :name="appendIcon"></sp-icon>
  </sp-text-field>
</template>

<script setup>
import { computed } from "vue";

import { booleanAttribute, toBoolean } from "#ce/utils/props";

const emit = defineEmits(["clear"]);

function reportClear() {
  emit("clear");
}

const props = defineProps({
  value: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
  prependIcon: {
    type: String,
    default: undefined,
  },
  appendIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the activator's input. Pass a catalog value from the
   * feature-specific filter (see `app/frontend/src/test-ids.json`).
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * When set, the activator hides its clear button so the value can never be
   * emptied. Used for legally required filters (see `<sp-required-filters-modal>`).
   */
  nonClearable: booleanAttribute(),
  disabled: booleanAttribute(),
});

const clearable = computed(() => !toBoolean(props.disabled) && !toBoolean(props.nonClearable));

const placeholder = computed(() => props.value ?? props.label);
</script>

<style scoped lang="scss">
@import "@assets/mixins/breakpoint";

.activator {
  // The compact-* tokens size the activator below sm; --sp-comp-default-filter-activator-height sizes it from sm up.
  --activator-height: var(--sp-comp-default-filter-activator-compact-height, 44px);
  --activator-border-color: var(--sp-comp-default-filter-activator-border-color, var(--sp-sys-form-field-border-color));
  --activator-border-width: var(--sp-comp-default-filter-activator-border-width, 1px);
  --activator-border-radius: var(--sp-comp-default-filter-activator-compact-border-radius, var(--sp-sys-shape-control));
  --activator-color: #656f75;
  --activator-prepend-icon-size: var(--sp-comp-icon-size, var(--sp-sys-icon-size-medium, 2rem));

  --sp-comp-text-field-color: var(--activator-color);
  --sp-comp-text-field-padding-inline: var(--sp-ref-scale-8);
  --sp-comp-text-field-input-padding-inline: var(--sp-comp-default-filter-activator-icon-gap);
  --sp-comp-text-field-line-height: var(--sp-comp-default-filter-activator-line-height);
  --sp-comp-text-field-placeholder-color: var(
    --sp-comp-default-filter-activator-placeholder-color,
    var(--sp-sys-form-field-border-color)
  );
  --sp-comp-text-field-background-color: white;
  --sp-comp-text-field-border-color: var(--activator-border-color);
  --sp-comp-text-field-border-width: var(--activator-border-width);
  --sp-comp-text-field-border-radius: var(--activator-border-radius);
  --sp-comp-text-field-min-height: var(--activator-height);
  // Without --sp-comp-default-filter-activator-compact-height this is invalid, so the text field uses its
  // own compact height, not an inherited one.
  --sp-comp-text-field-compact-min-height: var(--sp-comp-default-filter-activator-compact-height);
  --sp-comp-text-field-cursor: pointer;
  --sp-comp-text-field-content-padding-inline: var(
    --sp-comp-default-activator-text-field-padding-inline,
    var(--sp-ref-scale-10)
  );

  @include sp-breakpoint(sm) {
    --button-justify-content: start;
    --activator-height: var(--sp-comp-default-filter-activator-height, 3.625rem);
    --activator-border-radius: 0;
    --sp-comp-text-field-border-width: var(--activator-border-width);

    margin-bottom: 0;
  }

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
    box-shadow: none;
  }
}
.default-filter__activator-icon {
  --sp-comp-icon-size: var(--sp-comp-default-filter-activator-prepend-icon-size, var(--activator-prepend-icon-size));
  --fill: var(--sp-sys-color-primary);
}
.default-filter__append-icon {
  --fill: var(--sp-sys-form-field-placeholder-color);

  display: var(--sp-comp-default-filter-activator-append-icon-display, block);
}
</style>
